'use client';

import { useState, useCallback, useEffect } from 'react';
import Image from 'next/image';
import { X, Play, Pause, Maximize2, Minimize2 } from 'lucide-react';
import { type CarouselApi } from '@noirproperties/ui/components/ui/carousel';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Autoplay,
} from '@noirproperties/ui/components/ui/carousel';
import { Button } from '@noirproperties/ui/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@noirproperties/ui/components/ui/dialog';
import { Trans } from '@lingui/react/macro';
import { useGallery } from './gallery-context';

type GalleryImage = {
  id: string;
  url: string;
  tags: string[];
};

type GalleryProps = {
  images: { id: string; url: string; tags: string[] }[];
  title: string;
};

const PLACEHOLDER_SRC = `data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs%3D`;

function LazyLoadImage({
  image,
  inView,
  alt,
}: {
  image: GalleryImage;
  inView: boolean;
  alt: string;
}) {
  const [hasLoaded, setHasLoaded] = useState(false);

  const setLoaded = useCallback(() => {
    if (inView) setHasLoaded(true);
  }, [inView]);

  return (
    <div className="relative h-full w-full">
      {!hasLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-transparent" />
        </div>
      )}
      <Image
        alt={alt}
        className={`max-w-[100vw] object-contain transition-opacity duration-300 ${hasLoaded ? 'opacity-100' : 'opacity-0'}`}
        fill
        onLoad={setLoaded}
        priority={!inView}
        quality={100}
        sizes="100vw"
        src={inView ? image.url : PLACEHOLDER_SRC}
      />
    </div>
  );
}

export function Gallery({ images, title }: GalleryProps) {
  const { isOpen, openGallery, closeGallery, initialIndex } = useGallery();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [slidesInView, setSlidesInView] = useState<number[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleAutoplay = useCallback(() => {
    if (!api) return;
    const autoplayPlugin = api.plugins().autoplay;
    autoplayPlugin.isPlaying() ? autoplayPlugin.stop() : autoplayPlugin.play();
  }, [api]);

  const stopAutoplay = useCallback(() => {
    if (!api) return;
    const autoplayPlugin = api.plugins().autoplay;
    if (autoplayPlugin.isPlaying()) {
      autoplayPlugin.stop();
    }
  }, [api]);

  const handleScroll = useCallback(
    (direction: 'prev' | 'next') => {
      if (!api) return;
      direction === 'prev' ? api.scrollPrev() : api.scrollNext();
      stopAutoplay();
    },
    [api, stopAutoplay],
  );

  const updateSlidesInView = useCallback(() => {
    if (!api) return;
    setSlidesInView((prevSlidesInView) => {
      const inView = api
        .slidesInView()
        .filter((index) => !prevSlidesInView.includes(index));
      return [...prevSlidesInView, ...inView];
    });
  }, [api]);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!api) return;

    const onPlay = () => {
      setIsPlaying(true);
    };
    const onStop = () => {
      setIsPlaying(false);
    };
    const onSelect = () => {
      setCurrentIndex(api.selectedScrollSnap());
    };

    setCurrentIndex(initialIndex);

    api.on('select', onSelect);
    api.on('autoplay:play', onPlay);
    api.on('autoplay:stop', onStop);
    api.on('slidesInView', updateSlidesInView);
    api.on('reInit', updateSlidesInView);
    api.on('pointerDown', stopAutoplay);

    updateSlidesInView();

    return () => {
      api.off('select', onSelect);
      api.off('autoplay:play', onPlay);
      api.off('autoplay:stop', onStop);
      api.off('slidesInView', updateSlidesInView);
      api.off('reInit', updateSlidesInView);
      api.off('pointerDown', stopAutoplay);
    };
  }, [api, updateSlidesInView, stopAutoplay, initialIndex]);

  // Add keyboard navigation
  useEffect(() => {
    if (!isOpen || !api) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        api.scrollPrev();
        stopAutoplay();
      } else if (e.key === 'ArrowRight') {
        api.scrollNext();
        stopAutoplay();
      } else if (e.key === 'f') {
        void toggleFullscreen();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [api, isOpen, stopAutoplay, toggleFullscreen]);

  if (images.length === 0) return null;

  const additionalImages = images.length - 4;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Main large image */}
        <button
          className="relative aspect-[4/3] overflow-hidden rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            openGallery(0);
          }}
          type="button"
        >
          <Image
            alt={`${title} - Main Image`}
            className="h-full w-full object-cover"
            fill
            priority
            quality={95}
            src={images[0]?.url || ''}
          />
        </button>

        {/* Grid of smaller images */}
        <div className="grid grid-cols-2 gap-4">
          {images
            .slice(1, images.length > 4 ? 4 : undefined)
            .map((image, index) => (
              <button
                className="relative aspect-[4/3] overflow-hidden rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                key={image.id}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  openGallery(index + 1);
                }}
                type="button"
              >
                <Image
                  alt={`${title} - Image ${index + 2}`}
                  className="h-full w-full object-cover"
                  fill
                  quality={95}
                  src={image.url}
                />
              </button>
            ))}

          {/* View more button */}
          {images.length > 4 && (
            <button
              className="relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                openGallery(0);
              }}
              type="button"
            >
              <Image
                alt={`${title} - Image 4`}
                className="h-full w-full object-cover"
                fill
                quality={95}
                src={images[4]?.url || ''}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white transition hover:bg-black/70">
                <p className="text-lg font-semibold">
                  <Trans>+{additionalImages} photos</Trans>
                </p>
                <p className="text-sm">
                  <Trans>View All</Trans>
                </p>
              </div>
            </button>
          )}
        </div>
      </div>

      <Dialog onOpenChange={closeGallery} open={isOpen}>
        <DialogContent
          className="h-[100dvh] max-h-[100dvh] w-full max-w-[100dvw] overflow-hidden border-none p-0 [&>button:last-child]:hidden"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          <DialogHeader className="absolute left-0 right-0 top-0 z-10 flex-row items-center justify-between bg-gradient-to-b from-black/80 via-black/40 to-transparent px-8 py-6">
            <DialogTitle className="text-white">
              <p className="text-sm font-medium uppercase tracking-wider">
                <span className="text-white">{currentIndex + 1}</span>
                <span className="mx-2 text-white/40">/</span>
                <span className="text-white/40">{images.length}</span>
              </p>
            </DialogTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Button
                  className="rounded-full bg-black/20 backdrop-blur-sm hover:bg-black/40"
                  onClick={toggleAutoplay}
                  size="icon"
                  variant="ghost"
                >
                  {isPlaying ? (
                    <Pause className="h-4 w-4 text-white" />
                  ) : (
                    <Play className="h-4 w-4 text-white" />
                  )}
                  <span className="sr-only">
                    {isPlaying ? 'Pause slideshow' : 'Start slideshow'}
                  </span>
                </Button>
                {isPlaying ? (
                  <svg
                    className="pointer-events-none absolute inset-0 -m-0.5 h-[calc(100%+4px)] w-[calc(100%+4px)] rotate-[-90deg]"
                    viewBox="0 0 32 32"
                  >
                    <circle
                      className="stroke-white/20"
                      cx="16"
                      cy="16"
                      fill="none"
                      r="14"
                      strokeWidth="2"
                    />
                    <circle
                      className="animate-progress stroke-white"
                      cx="16"
                      cy="16"
                      fill="none"
                      r="14"
                      strokeDasharray="87.96"
                      strokeDashoffset="87.96"
                      strokeLinecap="round"
                      strokeWidth="2"
                    />
                  </svg>
                ) : null}
              </div>
              <Button
                className="hidden rounded-full bg-black/20 backdrop-blur-sm hover:bg-black/40 md:inline-flex"
                onClick={() => {
                  void toggleFullscreen();
                }}
                size="icon"
                variant="ghost"
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4 text-white" />
                ) : (
                  <Maximize2 className="h-4 w-4 text-white" />
                )}
                <span className="sr-only">
                  {isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                </span>
              </Button>
              <DialogClose asChild>
                <Button
                  className="rounded-full bg-black/20 backdrop-blur-sm hover:bg-black/40"
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <X className="h-4 w-4 text-white" />
                  <span className="sr-only">Close gallery</span>
                </Button>
              </DialogClose>
            </div>
          </DialogHeader>

          {/* Main Carousel */}
          <div className="flex h-full w-full items-center justify-center bg-black">
            <Carousel
              className="h-full w-full"
              opts={{
                align: 'center',
                loop: true,
                dragFree: false,
                startIndex: initialIndex,
              }}
              plugins={[
                Autoplay({
                  delay: 3000,
                  playOnInit: false,
                  stopOnInteraction: true,
                  stopOnLastSnap: true,
                }),
              ]}
              setApi={setApi}
            >
              <CarouselContent>
                {images.map((image, index) => (
                  <CarouselItem key={image.id}>
                    <div className="flex h-[100dvh] w-full items-center justify-center">
                      <div className="relative h-full w-full">
                        <LazyLoadImage
                          alt={`${title} - Image ${index + 1}`}
                          image={image}
                          inView={slidesInView.includes(index)}
                        />
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious
                className="absolute left-8 hidden h-14 w-14 rounded-full border border-white/10 bg-black/20 backdrop-blur-sm transition-colors hover:bg-black/40 md:flex"
                onClick={() => {
                  handleScroll('prev');
                }}
                variant="outline"
              />
              <CarouselNext
                className="absolute right-8 hidden h-14 w-14 rounded-full border border-white/10 bg-black/20 backdrop-blur-sm transition-colors hover:bg-black/40 md:flex"
                onClick={() => {
                  handleScroll('next');
                }}
                variant="outline"
              />
            </Carousel>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
